/*
 * @Description: Tauri HTTP 适配器 - 绕过 WebView CORS 限制
 * @Date: 2026-04-20 10:02:38
 * @LastEditTime: 2026-09-18 15:39:48
 * @FilePath: \hui_ai_ui\src\api\tauri.js
 */
import axios from 'axios';
import { invoke } from '@tauri-apps/api/core';

// 检测是否在 Tauri 环境中
const _isTauri = typeof window !== 'undefined' && ('__TAURI__' in window || '__TAURI_IPC__' in window);

/**
 * 构建完整 URL（含 query params）
 */
function buildUrl(config) {
  let url = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
  if (config.params && Object.keys(config.params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(config.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }
  return url;
}

/**
 * 扁平化 axios 的 headers 到 flat object
 * axios 0.24 的 headers 结构可能是 { post: {'Content-Type':'...'}, Accept: '...' }
 */
function flattenHeaders(headers, method) {
  const flat = {};
  if (!headers) return flat;

  const methodLower = (method || '').toLowerCase();

  Object.entries(headers).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // axios method-specific headers: { post: { Content-Type: '...' } }
      if (key.toLowerCase() === methodLower || axios.METHODS.includes(key.toUpperCase())) {
        Object.entries(value).forEach(([k, v]) => {
          if (typeof v === 'string' || typeof v === 'number') {
            flat[k] = String(v);
          }
        });
      }
    } else if (typeof value === 'string' || typeof value === 'number') {
      flat[key] = String(value);
    }
  });

  return flat;
}

/**
 * 构建 body 字符串
 */
function buildBodyString(config) {
  let rawData = config.data;

  // fallback: axios 0.24 custom adapter 下 data 可能是 undefined
  if ((rawData === undefined || rawData === null) && config.method && config.method.toLowerCase() !== 'get') {
    if (config.params) rawData = config.params;
  }

  if (!rawData) return null;

  if (typeof rawData === 'string') return rawData;
  if (rawData instanceof URLSearchParams) return rawData.toString();
  if (typeof rawData === 'object') return JSON.stringify(rawData);
  return String(rawData);
}

/**
 * 创建 Tauri 环境下的 Axios 适配器
 * 
 * 直接调用 Rust 端的 http_request 命令（reqwest 实现），
 * 完全绕过 @tauri-apps/plugin-http fetch API —— 后者在 Tauri IPC 层
 * 对 body 类型（Text/Bytes/Json）有额外序列化处理，会把 JSON 对象
 * 错误地转换成数组格式发送。
 * 
 * Rust http_request 命令的 body 就是纯 String，直接用 reqwest 发送，
 * 无任何中间转换，完全可靠。
 */
export function createTauriAxiosAdapter(axiosInstance) {
  axiosInstance.defaults.adapter = async function (config) {
    const url = buildUrl(config);
    const method = (config.method || 'get').toUpperCase();
    const headers = flattenHeaders(config.headers, method);
    const bodyStr = buildBodyString(config);

    // 构建 Rust http_request 命令参数
    const reqPayload = {
      req: {
        url,
        method,
        headers: Object.keys(headers).length > 0 ? headers : null,
        body: bodyStr || null,
        timeout: config.timeout ? Math.ceil(config.timeout / 1000) : null
      }
    };

    // console.log('[Tauri Adapter] 请求:', method, url);
    // console.log('[Tauri Adapter DEBUG] headers:', JSON.stringify(headers));
    // console.log('[Tauri Adapter DEBUG] body:', bodyStr);

    try {
      // 调 Rust 命令！不是 plugin-http fetch
      const resp = await invoke('http_request', reqPayload);

      // console.log('[Tauri Adapter DEBUG] 响应状态:', resp.status);
      // console.log('[Tauri Adapter DEBUG] 响应体(原始):', resp.body);

      // 解析响应体
      let responseData;
      const responseType = config.responseType || 'json';

      try {
        if (responseType === 'blob') {
          responseData = new Blob([new TextEncoder().encode(resp.body)]);
        } else if (responseType === 'arraybuffer') {
          responseData = new TextEncoder().encode(resp.body).buffer;
        } else if (responseType === 'text') {
          responseData = resp.body;
        } else {
          try {
            responseData = resp.body ? JSON.parse(resp.body) : null;
          } catch {
            responseData = resp.body;
          }
        }
      } catch (parseErr) {
        // console.warn('[Tauri Adapter] 响应体解析失败:', parseErr);
        responseData = resp.body;
      }

      const axiosResponse = {
        data: responseData,
        status: resp.status,
        statusText: resp.status >= 200 && resp.status < 300 ? 'OK' : 'Error',
        headers: resp.headers || {},
        config: config,
        request: {}
      };

      if (resp.status >= 400) {
        const error = new Error(`Request failed with status code ${resp.status}`);
        error.response = axiosResponse;
        error.config = config;
        return Promise.reject(error);
      }

      return axiosResponse;
    } catch (error) {
      // console.error('[Tauri Adapter] 请求失败:', error);
      if (!error.config) error.config = config;
      return Promise.reject(error);
    }
  };

  return axiosInstance;
}

/**
 * 全局 hook axios.create
 */
export function hookAxiosCreate() {
  const originalCreate = axios.create;
  axios.create = function (config) {
    const instance = originalCreate.call(axios, config);
    createTauriAxiosAdapter(instance);
    return instance;
  };
}

// ========== 自动初始化 ==========
if (_isTauri) {
  // console.log('[Tauri Adapter] 检测到 Tauri 环境，自动 hook axios.create');
  hookAxiosCreate();
}
